import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import ListingSectionWithMetadata from '../transports/listing-section-with-metadata-transport'

interface FetchSectionPageResult {
    sections: ListingSectionWithMetadata[]
    totalItems: number
}

interface SectionPaginationResult {
    allSections: ListingSectionWithMetadata[]
    totalItems: number | null
    loading: boolean
    error: Error | null
    hasNextPage: boolean
    currentPage: number
    nextPage: () => void
    reset: () => void
}

function useLazySectionedPagination(
    fetchPage: (page: number) => Promise<FetchSectionPageResult>,
    pageSize: number,
    autoFetch = true,
): SectionPaginationResult {
    const [currentPage, setCurrentPage] = useState(0)
    const [allSections, setAllSections] = useState<ListingSectionWithMetadata[]>([])
    const [totalItems, setTotalItems] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const hasInitialized = useRef(false)
    const loadedPagesRef = useRef<Set<number>>(new Set())
    const versionRef = useRef(0)

    const totalPages = totalItems === null ? null : Math.ceil(totalItems / pageSize)
    const hasNextPage = totalPages === null || currentPage < totalPages

    const nextPage = useCallback(async () => {
        if (loading || !hasNextPage) {
            return
        }

        const pageToFetch = currentPage + 1

        if (loadedPagesRef.current.has(pageToFetch)) {
            setCurrentPage(pageToFetch)
            return
        }

        const capturedVersion = versionRef.current

        // Immediately mark page as being fetched to prevent duplicate calls
        loadedPagesRef.current.add(pageToFetch)
        setLoading(true)
        setError(null)

        try {
            const result = await fetchPage(pageToFetch)
            if (versionRef.current !== capturedVersion) {
                return
            }
            setAllSections(prev => [...prev, ...result.sections])
            setTotalItems(result.totalItems)
            setCurrentPage(pageToFetch)
        } catch (error_) {
            if (versionRef.current !== capturedVersion) {
                return
            }
            // Remove from loaded pages if fetch failed
            loadedPagesRef.current.delete(pageToFetch)
            setError(error_ instanceof Error ? error_ : new Error('Failed to fetch page'))
        } finally {
            if (versionRef.current === capturedVersion) {
                setLoading(false)
            }
        }
    }, [currentPage, fetchPage, hasNextPage, loading])

    const reset = useCallback(() => {
        versionRef.current += 1
        setCurrentPage(0)
        setAllSections([])
        setTotalItems(null)
        setLoading(false)
        setError(null)
        loadedPagesRef.current.clear()
        hasInitialized.current = false
    }, [])

    useEffect(() => {
        if (autoFetch && !hasInitialized.current && currentPage === 0 && allSections.length === 0) {
            hasInitialized.current = true
            nextPage()
        }
    }, [autoFetch, currentPage, allSections.length, nextPage])

    useEffect(() => {
        if (autoFetch && !hasInitialized.current) {
            hasInitialized.current = true
            nextPage()
        }
    }, [autoFetch])

    return useMemo(
        () => ({
            allSections,
            totalItems,
            loading,
            error,
            hasNextPage,
            currentPage,
            nextPage,
            reset,
        }),
        [allSections, totalItems, loading, error, hasNextPage, currentPage, nextPage, reset],
    )
}

export default useLazySectionedPagination
